import { atLeastOneFieldNotEmpty } from '@brands/Dashboard/Dashboard/SuperAdmin/modal/Patient/validationSchema';
import dayjs from 'dayjs';
import parsePhoneNumberFromString, { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';

import { Organization } from '../../../../services/identity/types/UserProfile';
import {
  checkFutureDate,
  checkValidDate,
  isValidAddress,
  isValidDate,
  isValidEmail,
  isValidName,
  isValidZipCode,
} from '../../../../Utils/validation';

// For forms where an address is optional: an empty address passes, but once any
// address field is filled in, the rest of the address must be complete and valid.
const validateAddressFieldWhenPresent = (
  field: 'address1' | 'city' | 'state' | 'zip_code',
  value: unknown,
  parent: any,
): boolean => {
  const addressValues = [parent.address1, parent.address2, parent.city, parent.state, parent.zip_code];
  if (!atLeastOneFieldNotEmpty(addressValues)) return true;
  if (!value) return false;
  if (field === 'address1') return isValidAddress(String(value));
  if (field === 'zip_code') return isValidZipCode(String(value));
  return true;
};

export const profileValidationSchema = (
  reviewProfile: boolean | undefined,
  isAdmin: boolean | undefined,
  isFromSelectPatient: boolean | undefined,
  addressUnavailable = false,
  isOrganizationAdmin?: boolean,
  essentialFieldsOnly = false,
): any => {
  // `essentialFieldsOnly`: an admin creating a case for a patient who was never
  // invited to the platform only has to provide name and date of birth. Every
  // other field stays optional, but is still validated when it is filled in.
  const addressOptional = addressUnavailable || essentialFieldsOnly;

  return Yup.object().shape({
    profile_img: Yup.string(),
    first_name: Yup.string()
      .required('First name is required')
      .test('test-name', 'Enter Valid First Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    last_name: Yup.string()
      .required('Last name is required')
      .test('test-name', 'Enter Valid Last Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    // Admins can create a patient without a gender, so outside case intake
    // their edit form treats it as optional, same as gender identity below.
    // Requiring it here left a patient they had just created unsavable.
    gender:
      !essentialFieldsOnly && (!isAdmin || isFromSelectPatient)
        ? Yup.string()
            .required('Gender is required')
            .test('test-name', 'Gender is required', (value) => {
              if (value === '') {
                return false;
              }
              return true;
            })
        : Yup.string(),
    gender_identity:
      !essentialFieldsOnly && (!isAdmin || isFromSelectPatient)
        ? Yup.string()
            .required('Gender identity is required')
            .test('test-name', 'Gender identity is required', (value) => {
              if (value === '') {
                return false;
              }
              return true;
            })
        : Yup.string(),
    date_of_birth: Yup.string()
      .required('Date of Birth is required')
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    language: essentialFieldsOnly
      ? Yup.array()
      : Yup.array().min(1, 'Language is required').required('Language is required'),
    address1: addressOptional
      ? Yup.string().test('test-name', 'Enter Valid Address', function (value) {
          return validateAddressFieldWhenPresent('address1', value, this.parent);
        })
      : Yup.string()
          .required('Address is required')
          .test('test-name', 'Enter Valid Address', (value) => {
            if (value && !isValidAddress(value)) {
              return false;
            }
            return true;
          }),
    address2: Yup.string(),
    city: addressOptional
      ? Yup.string().test('test-required', 'City is required when an address is provided', function (value) {
          return validateAddressFieldWhenPresent('city', value, this.parent);
        })
      : Yup.string().required('City is required'),
    state: addressOptional
      ? Yup.string().test('test-required', 'State is required when an address is provided', function (value) {
          return validateAddressFieldWhenPresent('state', value, this.parent);
        })
      : Yup.string().required('State is required'),
    zip_code: addressOptional
      ? Yup.string().test('test-name', 'Enter Valid Zip Code', function (value) {
          return validateAddressFieldWhenPresent('zip_code', value, this.parent);
        })
      : Yup.string()
          .required('Zip Code is required')
          .test('test-name', 'Enter Valid Zip Code', (value) => {
            if (value && !isValidZipCode(value)) {
              return false;
            }
            return true;
          }),
    // The Email field only renders on this form for `reviewProfile && isAdmin` (see
    // EditProfile.tsx's `{reviewProfile && isAdminRole && <TextField name="email" .../>}`)
    // — for every other role (e.g. a CareAdvisor completing a Care Coordination
    // patient's profile to create a case), there's no email input on the page at
    // all, so requiring it unconditionally here blocked submission with an "Email
    // is required" error the user had no field to fix. A patient without an email
    // (never sent an invitation) is a valid case-creation subject.
    email:
      reviewProfile && isAdmin && !essentialFieldsOnly
        ? Yup.string()
            .required('Email is required')
            .test('test-name', 'Enter Valid Email', (value) => {
              if (value && !isValidEmail(value)) {
                return false;
              }
              return true;
            })
        : Yup.string().test('test-name', 'Enter Valid Email', (value) => {
            if (value && !isValidEmail(value)) {
              return false;
            }
            return true;
          }),
    // Everyone belongs to Dentistry One, so there's no organization input to require; `organizations`
    // is the patient's hydrated record, kept here because the client tag rule below reads it.
    organizations: Yup.array(),
    // Client Tag only renders on this form when `reviewProfile` is true, the editor isn't an
    // OrganizationAdmin, and the patient belongs to the `dentistry-one` organization (see
    // EditProfile.tsx's Client tags block) — required-ness here mirrors that same visibility
    // so submission isn't blocked by a field the current user can't see or fix.
    clientTag:
      reviewProfile && !isOrganizationAdmin
        ? Yup.array().when('organizations', {
            is: (organizations: Organization[]) => organizations?.some((org) => org.id === 'dentistry-one'),
            then: Yup.array().min(1, 'Client Tag is required'),
            otherwise: Yup.array(),
          })
        : Yup.array(),
    phone_number: essentialFieldsOnly
      ? Yup.string()
          .nullable()
          .test('test-name', 'Enter Valid Phone Number', (value) => {
            if (!value) return true;

            return parsePhoneNumberFromString(value, 'US')?.isValid() ?? false;
          })
      : Yup.string()
          .required('Phone number is required')
          .test('test-name', 'Enter Valid Phone Number', (value) => {
            if (!value) return false; // Ensure value exists

            const phoneNumber = parsePhoneNumberFromString(value, 'US'); // Parse the number
            return phoneNumber?.isValid() ?? false; // Validate it
          }),
  });
};

export const dependentAddValidationSchema = (
  addSecondaryInsurance: boolean | undefined,
  isAdminRole: boolean | undefined,
): any => {
  return Yup.object().shape({
    profile_img: Yup.string(),
    first_name: Yup.string()
      .required('First name is required')
      .test('test-name', 'Enter Valid First Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    dependent_email: Yup.string().test('test-name', 'Enter Valid Email', (value) => {
      if (value?.trim() && !isValidEmail(value)) {
        return false;
      }
      return true;
    }),
    phone_number: Yup.string().test('test-name', 'Enter Valid Phone Number', (value) => {
      if (value?.trim() && !isValidPhoneNumber(value, 'US')) {
        return false;
      }
      return true;
    }),
    last_name: Yup.string()
      .required('Last name is required')
      .test('test-name', 'Enter Valid Last Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    gender: Yup.string(),
    gender_identity: !isAdminRole
      ? Yup.string()
          .required('Gender identity is required')
          .test('test-name', 'Gender identity is required', (value) => {
            if (value === '') {
              return false;
            }
            return true;
          })
      : Yup.string(),
    date_of_birth: Yup.string()
      .required('Date of Birth is required')
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    language: Yup.array(),
    address1: Yup.string().test(
      'test-name',
      'Valid Address is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!address1 && isValidAddress(address1);
        }
        return true;
      },
    ),
    address2: Yup.string(),
    city: Yup.string().test(
      'test-required',
      'City is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!city;
        }
        return true;
      },
    ),
    state: Yup.string().test(
      'test-required',
      'State is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!state;
        }
        return true;
      },
    ),
    zip_code: Yup.string().test(
      'test-name',
      'Valid Zip Code is required when at least one Address field is provided',
      function () {
        const { parent } = this;
        const { address1, address2, city, state, zip_code } = parent as any;
        if (atLeastOneFieldNotEmpty([address1, address2, city, state, zip_code])) {
          return !!zip_code && isValidZipCode(zip_code);
        }
        return true;
      },
    ),
    guardian_relationship: Yup.string().required('Relationship is required'),
    insurance_through: Yup.string(),
    insurance_carrier_id: Yup.number().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== undefined,
      then: Yup.number().required('Insurance carrier is required'),
      otherwise: Yup.number(),
    }),
    iPhoneNumber: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== undefined,
      then: Yup.string().test('test-name', 'Enter Valid Phone Number', (value: any) => {
        if (!value || !isValidPhoneNumber(value)) {
          return false;
        }
        return true;
      }),
      otherwise: Yup.string(),
    }),
    member_id: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Member ID is required')
        .test('no-empty-space', 'Member ID cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    group_number: Yup.string().nullable(),
    insurance_address: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance Address is required')
        .test('no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_city: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance City is required')
        .test('no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_state: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance State is required')
        .test('no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_zip_code: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined,
        then: Yup.string().required('Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    iMiddleName: Yup.string(),
    iFirstName: Yup.string().when(['insurance_through'], {
      is: (val: string) => {
        return val !== 'none' && val !== undefined && val !== 'myself';
      },
      then: Yup.string().required('Insurance Holder First Name is required'),
    }),
    iLastName: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder Last Name is required'),
    }),
    iDateOfBirth: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder Date of birth is required'),
      })
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    iAddress1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder Address is required'),
    }),
    iCity: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder city Required'),
    }),
    iState: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder state Required'),
    }),
    iZipCode: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    state_funded_coverage_status: Yup.string().when('insurance_through', {
      is: (val: string) => val && val.trim() !== '' && val !== 'none',
      then: Yup.string().required(
        'Do you currently have insurance coverage through a state-funded program such as Medicaid?**',
      ),
      otherwise: Yup.string(),
    }),
    secondary_insurance_through: Yup.string().test(
      'secondary_insurance_through',
      'Secondary Insurance is required',
      (value) => {
        return addSecondaryInsurance ? value !== undefined && value?.trim() !== '' : true;
      },
    ),
    secondary_insurance_carrier_id: Yup.number().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.number().required('Secondary Insurance carrier ID is required'),
      otherwise: Yup.number(),
    }),
    secondary_member_id: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string().required('Secondary Member ID is required'),
      otherwise: Yup.string(),
    }),
    secondary_group_number: Yup.string().nullable(),
    secondary_iPhoneNumber: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),

    secondary_insurance_address: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Secondary Insurance Address is required')
        .test('no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance City is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance State is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && addSecondaryInsurance,
        then: Yup.string().required('Secondary Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    secondary_iMiddleName: Yup.string(),
    secondary_iFirstName: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder First Name is required')
        .test('test-name', 'Enter Valid First Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iLastName: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Last Name is required')
        .test('test-name', 'Enter Valid Last Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iDateOfBirth: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Date of birth is required')
        .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
          if (value && !isValidDate(value)) {
            return false;
          }
          if (value) {
            const datevalues = value?.split('/');
            if (!checkValidDate(datevalues[0], datevalues[1], datevalues[2])) {
              return false;
            }
          }
          return true;
        })
        .test('test-future', 'The Secondary Date of Birth can not be in the future.', (value) => {
          if (value) {
            const datevalues = value?.split('/');
            if (!checkFutureDate(datevalues[0], datevalues[1], datevalues[2])) {
              return false;
            }
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iAddress1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder Address is required'),
      otherwise: Yup.string(),
    }),
    secondary_iCity: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder city Required'),
      otherwise: Yup.string(),
    }),
    secondary_iState: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder state Required'),
      otherwise: Yup.string(),
    }),
    secondary_iZipCode: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder ZipCode is required')
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
  });
};

export const dependentValidationSchemaNewDesign = (addSecondaryInsurance: boolean | undefined): any => {
  return Yup.object().shape({
    profile_img: Yup.string(),
    first_name: Yup.string()
      .required('First name is required')
      .test('test-name', 'Enter Valid First Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    dependent_email: Yup.string().test('test-name', 'Enter Valid Email', (value) => {
      if (value?.trim() && !isValidEmail(value)) {
        return false;
      }
      return true;
    }),
    last_name: Yup.string()
      .required('Last name is required')
      .test('test-name', 'Enter Valid Last Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    gender: Yup.string()
      .required('Gender is required')
      .test('test-name', 'Gender is required', (value) => {
        if (value === '') {
          return false;
        }
        return true;
      }),
    gender_identity: Yup.string()
      .required('Gender identity is required')
      .test('test-name', 'Gender identity is required', (value) => {
        if (value === '') {
          return false;
        }
        return true;
      }),
    date_of_birth: Yup.string()
      .required('Date of Birth is required')
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    language: Yup.array().min(1, 'Language is required').required('Language is required'),
    address1: Yup.string()
      .required('Address is required')
      .test('test-name', 'Enter Valid Address', (value) => {
        if (value && !isValidAddress(value)) {
          return false;
        }
        return true;
      }),
    address2: Yup.string(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip_code: Yup.string()
      .required('Zip Code is required')
      .test('test-name', 'Enter Valid Zip Code', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    guardian_relationship: Yup.string().required('Relationship is required'),
    insurance_through: Yup.string().required('Insured by is required'),
    insurance_carrier_id: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        // Treat 0 as "empty" for validation purposes
        return originalValue === 0 ? null : value;
      })
      .when('insurance_through', {
        is: (val: string) => val && val !== 'none' && val !== '',
        then: (schema) =>
          schema.typeError('Insurance carrier ID is required').required('Insurance carrier ID is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    insurance_phone_number: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== undefined && val !== '',
      then: Yup.string().test('test-name', 'Enter Valid Phone Number', (value: any) => {
        if (!value || !isValidPhoneNumber(value)) {
          return false;
        }
        return true;
      }),
      otherwise: Yup.string(),
    }),
    member_id: Yup.string().when(['insurance_through'], {
      is: (val: string) => !!val && val !== 'none' && val !== undefined && val !== '',
      then: Yup.string()
        .required('Member ID is required')
        .test('no-empty-space', 'Member ID cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    group_number: Yup.string().nullable(),
    insurance_address: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '',
      then: Yup.string()
        .required('Insurance Address is required')
        .test('no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_city: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '',
      then: Yup.string()
        .required('Insurance City is required')
        .test('no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_state: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '',
      then: Yup.string()
        .required('Insurance State is required')
        .test('no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_zip_code: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== '',
        then: Yup.string().required('Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    insurance_first_name: Yup.string().when(['insurance_through'], {
      is: (val: string) => {
        return val !== 'none' && val !== undefined && val !== '' && val !== 'myself';
      },
      then: Yup.string().required('Insurance Holder First Name is required'),
    }),
    insurance_last_name: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
      then: Yup.string().required('Insurance Holder Last Name is required'),
    }),
    insurance_date_of_birth: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
        then: Yup.string().required('Insurance Holder Date of birth is required'),
      })
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        const formattedDate = value ? dayjs(value).format('MM/DD/YYYY') : '';
        if (value && !isValidDate(formattedDate)) {
          return false;
        }
        if (value) {
          const values = formattedDate?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const formattedDate = dayjs(value).format('MM/DD/YYYY');
          const values = formattedDate?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    insurance_address1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
      then: Yup.string().required('Insurance Holder Address is required'),
    }),
    insurance_city1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
      then: Yup.string().required('Insurance Holder city Required'),
    }),
    insurance_state1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
      then: Yup.string().required('Insurance Holder state Required'),
    }),
    insurance_zip_code1: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
        then: Yup.string().required('Insurance Holder ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    insurance_gender: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && val !== 'myself',
      then: Yup.string()
        .required('Insurance Holder Gender is required')
        .test('test-name', 'Enter Valid Gender', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    state_funded_coverage_status: Yup.string().when('insurance_through', {
      is: (val: string) => val && val.trim() !== '' && val !== 'none',
      then: Yup.string().required(
        'Do you currently have insurance coverage through a state-funded program such as Medicaid?',
      ),
      otherwise: Yup.string(),
    }),
    secondary_insurance_through: Yup.string().test(
      'secondary_insurance_through',
      'Secondary Insurance is required',
      (value) => {
        return addSecondaryInsurance ? value !== undefined && value?.trim() !== '' : true;
      },
    ),
    secondary_insurance_carrier_id: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
        then: (schema) => schema.required('Secondary Insurance carrier ID is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    secondary_member_id: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Member ID is required'),
      otherwise: Yup.string(),
    }),
    secondary_group_number: Yup.string().nullable(),
    secondary_insurance_phone_number: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),

    secondary_insurance_address: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Address is required')
        .test('no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance City is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance State is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== '' && addSecondaryInsurance,
        then: Yup.string().required('Secondary Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    secondary_insurance_first_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder First Name is required')
        .test('test-name', 'Enter Valid First Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_last_name: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Last Name is required')
        .test('test-name', 'Enter Valid Last Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_date_of_birth: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Date of birth is required')
        .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
          if (value && !isValidDate(value)) {
            return false;
          }
          if (value) {
            const datevalues = value?.split('/');
            if (!checkValidDate(datevalues[0], datevalues[1], datevalues[2])) {
              return false;
            }
          }
          return true;
        })
        .test('test-future', 'The Secondary Date of Birth can not be in the future.', (value) => {
          if (value) {
            const datevalues = value?.split('/');
            if (!checkFutureDate(datevalues[0], datevalues[1], datevalues[2])) {
              return false;
            }
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_gender: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Gender is required')
        .test('test-name', 'Enter Valid Gender', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_address1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder Address is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder city Required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder state Required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) =>
        val !== 'none' && val !== undefined && val !== '' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder ZipCode is required')
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
  });
};

export const dependentUpdateValidationSchema = (
  addSecondaryInsurance: boolean | undefined,
  isAdminRole: boolean | undefined,
  isFromSelectPatient: boolean | undefined,
  essentialFieldsOnly = false,
): any => {
  // `essentialFieldsOnly`: see profileValidationSchema. For a dependent the
  // guardian relationship is required on top of name and date of birth.
  return Yup.object().shape({
    profile_img: Yup.string(),
    first_name: Yup.string()
      .required('First name is required')
      .test('test-name', 'Enter Valid First Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    middle_name: Yup.string().test('test-name', 'Enter Valid Middle Name', (value) => {
      if (value && !isValidName(value)) {
        return false;
      }
      return true;
    }),
    dependent_email: Yup.string().test('test-name', 'Enter Valid Email', (value) => {
      if (value?.trim() && !isValidEmail(value)) {
        return false;
      }
      return true;
    }),
    // Nullable: a dependent with a deleted phone comes back from the API as
    // null, and a non-nullable string() would hard-fail the whole save.
    phone_number: Yup.string()
      .nullable()
      .test('test-name', 'Enter Valid Phone Number', (value) => {
        if (value?.trim() && !isValidPhoneNumber(value, 'US')) {
          return false;
        }
        return true;
      }),

    guardian_relationship:
      !isAdminRole || isFromSelectPatient ? Yup.string().required('Relationship is required') : Yup.string(),
    last_name: Yup.string()
      .required('Last name is required')
      .test('test-name', 'Enter Valid Last Name', (value) => {
        if (value && !isValidName(value)) {
          return false;
        }
        return true;
      }),
    gender: essentialFieldsOnly
      ? Yup.string()
      : Yup.string()
          .required('Gender is required')
          .test('test-name', 'Gender is required', (value) => {
            if (value === '') {
              return false;
            }
            return true;
          }),
    gender_identity:
      !essentialFieldsOnly && (!isAdminRole || isFromSelectPatient)
        ? Yup.string()
            .required('Gender identity is required')
            .test('test-name', 'Gender identity is required', (value) => {
              if (value === '') {
                return false;
              }
              return true;
            })
        : Yup.string(),
    date_of_birth: Yup.string()
      .required('Date of Birth is required')
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    language: essentialFieldsOnly
      ? Yup.array()
      : Yup.array().min(1, 'Language is required').required('Language is required'),
    address1: essentialFieldsOnly
      ? Yup.string().test('test-name', 'Enter Valid Address', function (value) {
          return validateAddressFieldWhenPresent('address1', value, this.parent);
        })
      : Yup.string()
          .required('Address is required')
          .test('test-name', 'Enter Valid Address', (value) => {
            if (value && !isValidAddress(value)) {
              return false;
            }
            return true;
          }),
    address2: Yup.string(),
    city: essentialFieldsOnly
      ? Yup.string().test('test-required', 'City is required when an address is provided', function (value) {
          return validateAddressFieldWhenPresent('city', value, this.parent);
        })
      : Yup.string().required('City is required'),
    state: essentialFieldsOnly
      ? Yup.string().test('test-required', 'State is required when an address is provided', function (value) {
          return validateAddressFieldWhenPresent('state', value, this.parent);
        })
      : Yup.string().required('State is required'),
    zip_code: essentialFieldsOnly
      ? Yup.string().test('test-name', 'Enter Valid Zip Code', function (value) {
          return validateAddressFieldWhenPresent('zip_code', value, this.parent);
        })
      : Yup.string()
          .required('Zip Code is required')
          .test('test-name', 'Enter Valid Zip Code', (value) => {
            if (value && !isValidZipCode(value)) {
              return false;
            }
            return true;
          }),
  });
};

export const pharmacyValidationSchema = (): any => {
  return Yup.object().shape({
    pharmacy_zip_code: Yup.string()
      .nullable()
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    pharmacy_phone_number: Yup.string()
      .nullable()
      .test('test-name', 'Enter Valid Phone Number', (value) => {
        if (value && !isValidPhoneNumber(value)) {
          return false;
        }
        return true;
      }),
  });
};

export const insuranceValidationSchema = (addSecondaryInsurance: boolean): any => {
  return Yup.object().shape({
    insurance_through: Yup.string().required('This field is required'),
    insurance_carrier_id: Yup.number().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.number()
        .transform((value, originalValue) => (originalValue === null ? undefined : value))
        .required('Insurance carrier is required'),
      otherwise: Yup.number(),
    }),
    iPhoneNumber: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    member_id: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Member ID is required')
        .test('no-empty-space', 'Member ID cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    ggroup_number: Yup.string().nullable(),
    insurance_address: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance Address is required')
        .test('no-empty-space', 'Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_city: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance City is required')
        .test('no-empty-space', 'Insurance City cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_state: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined,
      then: Yup.string()
        .required('Insurance State is required')
        .test('no-empty-space', 'Insurance State cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    insurance_zip_code: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined,
        then: Yup.string().required('Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    iMiddleName: Yup.string(),
    iFirstName: Yup.string().when(['insurance_through'], {
      is: (val: string) => {
        return val !== 'none' && val !== undefined && val !== 'myself';
      },
      then: Yup.string().required('Insurance Holder First Name is required'),
    }),
    iLastName: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder Last Name is required'),
    }),
    iDateOfBirth: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder Date of birth is required'),
      })
      .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
        if (value && !isValidDate(value)) {
          return false;
        }
        if (value) {
          const values = value?.split('/');
          if (!checkValidDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      })
      .test('test-future', 'The Date of Birth can not be in the future.', (value) => {
        if (value) {
          const values = value?.split('/');
          if (!checkFutureDate(values[0], values[1], values[2])) {
            return false;
          }
        }
        return true;
      }),
    iAddress1: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder Address is required'),
    }),
    iCity: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder city Required'),
    }),
    iState: Yup.string().when(['insurance_through'], {
      is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
      then: Yup.string().required('Insurance Holder state Required'),
    }),
    iZipCode: Yup.string()
      .when(['insurance_through'], {
        is: (val: string) => val !== 'none' && val !== undefined && val !== 'myself',
        then: Yup.string().required('Insurance Holder ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    state_funded_coverage_status: Yup.string().when('insurance_through', {
      is: (val: string) => val && val.trim() !== '' && val !== 'none',
      then: Yup.string().required(
        'Do you currently have insurance coverage through a state-funded program such as Medicaid?',
      ),
      otherwise: Yup.string(),
    }),
    secondary_insurance_through: Yup.string().test(
      'secondary_insurance_through',
      'Secondary Insurance is required',
      (value) => {
        return addSecondaryInsurance ? value?.trim() !== '' : true;
      },
    ),
    secondary_insurance_carrier_id: Yup.number().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && addSecondaryInsurance,
      then: Yup.number()
        .required('Secondary Insurance carrier ID is required')
        .test('notZero', 'Secondary Insurance carrier ID is required', function (value) {
          return value !== 0;
        }),
      otherwise: Yup.number(),
    }),
    secondary_member_id: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Member ID is required'),
      otherwise: Yup.string(),
    }),
    secondary_group_number: Yup.string().nullable(),
    secondary_insurance_address: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Address is required')
        .test('no-empty-space', 'Secondary Insurance Address cannot be empty spaces', (value) => {
          if (value) {
            return !/^\s+$/.test(value);
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_insurance_city: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance City is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_state: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance State is required'),
      otherwise: Yup.string(),
    }),
    secondary_insurance_zip_code: Yup.string()
      .when(['secondary_insurance_through'], {
        is: (val: string) => val !== 'none' && addSecondaryInsurance,
        then: Yup.string().required('Secondary Insurance ZipCode is required'),
      })
      .test('test-name', 'Not valid zipcode', (value) => {
        if (value && !isValidZipCode(value)) {
          return false;
        }
        return true;
      }),
    secondary_iPhoneNumber: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance phone number is required')
        .test('test-name', 'Enter Valid Phone Number', (value) => {
          if (value && !isValidPhoneNumber(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iFirstName: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder First Name is required')
        .test('test-name', 'Enter Valid First Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iMiddleName: Yup.string(),
    secondary_iLastName: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Last Name is required')
        .test('test-name', 'Enter Valid Last Name', (value) => {
          if (value && !isValidName(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iDateOfBirth: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder Date of birth is required')
        .test('test-name', 'Enter Valid Date  MM/DD/YYYY', (value) => {
          if (value && !isValidDate(value)) {
            return false;
          }
          if (value) {
            const datevalues = value?.split('/');
            if (!checkValidDate(datevalues[0], datevalues[1], datevalues[2])) {
              return false;
            }
          }
          return true;
        })
        .test('test-future', 'The Secondary Date of Birth can not be in the future.', (value) => {
          if (value) {
            const datevalues = value?.split('/');
            if (!checkFutureDate(datevalues[0], datevalues[1], datevalues[2])) {
              return false;
            }
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
    secondary_iAddress1: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder Address is required'),
      otherwise: Yup.string(),
    }),
    secondary_iCity: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder city Required'),
      otherwise: Yup.string(),
    }),
    secondary_iState: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string().required('Secondary Insurance Holder state Required'),
      otherwise: Yup.string(),
    }),
    secondary_iZipCode: Yup.string().when(['secondary_insurance_through'], {
      is: (val: string) => val !== 'none' && val !== 'myself' && addSecondaryInsurance,
      then: Yup.string()
        .required('Secondary Insurance Holder ZipCode is required')
        .test('test-name', 'Not valid zipcode', (value) => {
          if (value && !isValidZipCode(value)) {
            return false;
          }
          return true;
        }),
      otherwise: Yup.string(),
    }),
  });
};
